import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import { searchBook } from '../../utils/api/bookApi';
import { mapBinding } from '../../utils/book/bindingHelper';
import { getAuthors } from '../../utils/book';
import { captureException } from '../../utils/sentry';

import './index.less';

export const SearchItem = ({ book, onSelect, isSelected = false }) => {
  const onClick = () => onSelect(book);

  const {
    imageUrl,
    title,
    edition = null,
    binding = 'OTHER',
    publisher = null,
    publishedYear = null,
  } = book;

  return <div onClick={onClick} className={`searchitem ${isSelected ? 'searchitem--selected' : ''}`}>
    <div className="searchitem__cover">
      <img src={imageUrl} alt={title}/>
    </div>
    <div className="searchitem__metadata">
      <h4>{title} ({edition ? `${edition}.` : 'Ukjent'} utgave)</h4>
      <p className="small"><em>{getAuthors(book).join(', ')}</em></p>
      <p className="small">
        <span>{mapBinding(binding)}, </span>
        {publisher && <span>{publisher}, </span>}
        <span>{publishedYear}</span>
      </p>

      <button className="button searchitem__button" onClick={onClick} disabled={isSelected}>
        {isSelected ? 'Valgt' : 'Velg'}
      </button>
    </div>
  </div>;
};

const doNothing = (evt) => {
  evt.preventDefault();
};

const stripIsbn = (query) => query.replace(/[- ]/g, '');
const isIsbnQuery = (query) => /^\d{10}$|^\d{13}$/.test(stripIsbn(query));

class BookAutocomplete extends React.Component {
  state = {
    query: this.props.initialQuery || '',
    results: [],
    hasNoResults: false,
  };

  changeQuery = (evt) => {
    const query = evt.target.value;

    this.setState({ query, hasNoResults: false });

    this.getSuggestions(query);
  };

  /**
   * Calls the autocomplete function
   * at most once every 300ms, to prevent
   * us from spamming the server.
   * */
  getSuggestions = debounce((query) => {
    // Search string should be at least 3 characters
    // before we start quering server
    if (query.trim().length < 3) {
      this.setState({ results: [], hasNoResults: false });
      return;
    }

    searchBook(query)
      .then((results) => {
        const normalizedQuery = stripIsbn(query);
        const exactMatch = results.find((result) => `${result.isbn}` === normalizedQuery);

        if (isIsbnQuery(query) && (exactMatch || results.length === 1)) {
          this.selectItem(exactMatch || results[0]);
          return;
        }

        this.setState({
          results,
          hasNoResults: results.length === 0,
        });
      })
      .catch((err) => captureException(err, null, { 'search.query': query }));
  }, 300);

  selectItem = (item) => {
    // Notify parent prop that an item has been selected
    this.props.onSelect(item);
    this.setState({ results: [], hasNoResults: false });
  };

  render() {
    const { selectedItem } = this.props;
    const { results, hasNoResults } = this.state;

    return <div>
      <form className="searchform" onSubmit={doNothing}>
        <div className="searchform__input-row">
          <input type="search"
                 name="query"
                 placeholder="Søk etter tittel/ISBN"
                 onChange={this.changeQuery}
                 value={this.state.query}
          />
          <a href="/strekkode?redirect=sett_inn_annonse" className="button searchform__scan" title="Scan strekkode">
            <span>Scan strekkode</span>
            <img src="/static/img/barcode.svg" alt=""/>
          </a>
        </div>

        {this.props.hint ? this.props.hint : null}
      </form>

      {hasNoResults && <p className="searchform__empty" role="status">Ingen resultater for søket ditt.</p>}

      {results.length > 0 && <div className="searchresults">
        <h3>Søkeresultat</h3>
        {results.map((result, key) => <div key={`search-${key}-${result.isbn}`}>
          <SearchItem book={result}
                      onSelect={this.selectItem}
                      isSelected={selectedItem && selectedItem.isbn === result.isbn}/>
        </div>)}
      </div>}
    </div>;
  }
}

BookAutocomplete.propTypes = {
  onSelect: PropTypes.func.isRequired,
  selectedItem: PropTypes.shape({
    isbn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
  initialQuery: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hint: PropTypes.any,
};

export default BookAutocomplete;
